import { ButtonType } from "@constellation-academy/epic-ui";
import { useFormik } from "formik";
import { useState } from "react";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { useTranslation } from "@i18n/hooks";
import { personalizedNotificationTemplateFormSchema } from "./personalized-notification-template-form.schema";
import {
	variablesListClass,
	variablesWrapperClass,
} from "./personalized-notification-template-form.styles";
import type {
	PersonalizedNotificationTemplateFormProps,
	PersonalizedNotificationTemplateFormValues,
} from "./personalized-notification-template-form.types";
import { Button, ButtonVariant } from "../../../../components/button";
import { DefaultTextFieldComponent } from "../../../../components/DefaultTextInput";
import { ValidatedField } from "../../../../components/ValidatedField";
import { H2Span, P2Span } from "../../../../themes/font-tags";
import { PERSONALIZED_NOTIFICATION_TEMPLATE_VARIABLES } from "../../../../translations/personalized-notification-template";

export const PersonalizedNotificationTemplateForm = ({
	initialValues,
	onSubmit,
	variables,
	isSubmitting,
}: PersonalizedNotificationTemplateFormProps) => {
	const { t } = useTranslation("common");
	const [currentFocusedElement, setCurrentFocusedElement] = useState<HTMLInputElement | null>(
		null,
	);

	const form = useFormik<PersonalizedNotificationTemplateFormValues>({
		initialValues,
		validationSchema: toFormikValidationSchema(personalizedNotificationTemplateFormSchema),
		onSubmit,
	});

	const handleOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
		setCurrentFocusedElement(e.target);
	};

	const createVariableOnClickHandler = (variable: string) => () => {
		if (!currentFocusedElement) return;

		const fieldName = currentFocusedElement.getAttribute(
			"name",
		) as keyof PersonalizedNotificationTemplateFormValues;
		const fieldValue = form.values[fieldName];

		const cursorPosition = currentFocusedElement.selectionStart ?? 0;
		const variableInsertionText = `%%{${variable}}%%`;

		const newValue =
			fieldValue.slice(0, cursorPosition) +
			variableInsertionText +
			fieldValue.slice(cursorPosition);

		form.setFieldValue(fieldName, newValue);
		if (currentFocusedElement) {
			currentFocusedElement.focus();

			const newCursorPosition = cursorPosition + variableInsertionText.length;
			currentFocusedElement.setSelectionRange(newCursorPosition, newCursorPosition);
		}
	};

	const renderVariableButton = (variable: string) => (
		<Button
			key={variable}
			type={ButtonType.Button}
			variant={ButtonVariant.Success}
			onClick={createVariableOnClickHandler(variable)}
			label={`%%${variable}%% - ${PERSONALIZED_NOTIFICATION_TEMPLATE_VARIABLES[variable]}`}
		/>
	);

	return (
		<form onSubmit={form.handleSubmit}>
			<ValidatedField<PersonalizedNotificationTemplateFormValues, string>
				formikConfig={form}
				component={(props) => (
					<DefaultTextFieldComponent {...props} onFocus={handleOnFocus} />
				)}
				name="title"
				label={t("general.title")}
			/>
			<ValidatedField<PersonalizedNotificationTemplateFormValues, string>
				formikConfig={form}
				component={(props) => (
					<DefaultTextFieldComponent {...props} onFocus={handleOnFocus} />
				)}
				name="message"
				label={t("personalizedNotificationTemplates.messageLabel")}
			/>
			<div className={variablesWrapperClass}>
				<H2Span>{t("personalizedNotificationTemplates.availableVariables")}</H2Span>
				<P2Span>
					{t("personalizedNotificationTemplates.variablesDescription")}
					<br />
					{t("personalizedNotificationTemplates.variablesClickDescription")}
				</P2Span>
				<div className={variablesListClass}>{variables.map(renderVariableButton)}</div>
			</div>
			<Button type={ButtonType.Submit} disabled={isSubmitting} label={t("actions.save")} />
		</form>
	);
};
